<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import NucleoWorkOrderActionDialog from '../components/NucleoWorkOrderActionDialog.vue';
import NucleoWorkOrderDocuments from '../components/NucleoWorkOrderDocuments.vue';
import NucleoWorkOrderHistory from '../components/NucleoWorkOrderHistory.vue';
import NucleoWorkOrderPropertyDocuments from '../components/NucleoWorkOrderPropertyDocuments.vue';
import NucleoWorkOrderServiceExecutionDialog from '../components/NucleoWorkOrderServiceExecutionDialog.vue';
import NucleoWorkOrderServices from '../components/NucleoWorkOrderServices.vue';
import NucleoWorkOrderSummary from '../components/NucleoWorkOrderSummary.vue';
import { useNucleoWorkOrderDetail } from '../composables/useNucleoWorkOrderDetail';
import { useNucleoWorkOrderDetailActions } from '../composables/useNucleoWorkOrderDetailActions';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const listingId = computed(() => route.params.imovelListingId);
const orderId = computed(() => route.params.workOrderId);
const state = useNucleoWorkOrderDetail({
  listingId,
  orderId,
  notify: useAlert,
  t,
});
const {
  dialog,
  executionService,
  goBack,
  openDialog,
  closeDialog,
  openExecution,
  closeExecution,
  saveAction,
  saveExecution,
  acceptPayload,
  viewProperty,
} = useNucleoWorkOrderDetailActions({ route, router, state });
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('NUCLEO_WORK_ORDERS.DETAIL.TITLE')"
      :subtitle="t('NUCLEO_WORK_ORDERS.DETAIL.SUBTITLE')"
      :breadcrumb="[
        { label: t('NUCLEO_WORK_ORDERS.TITLE') },
        { label: t('NUCLEO_WORK_ORDERS.DETAIL.TITLE') },
      ]"
      show-back
      :back-label="t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.BACK')"
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="state.loading.value"
          @click="state.load"
        >
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="state.detail.value?.finalizar_pedido"
          icon="i-lucide-check"
          @click="openDialog(acceptPayload)"
        >
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.ACCEPT') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>
    <main
      class="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <div v-if="state.loading.value" class="space-y-4">
        <div
          v-for="index in 4"
          :key="index"
          class="h-32 animate-pulse rounded-lais bg-white"
        />
      </div>
      <p
        v-else-if="state.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ state.error.value }}
      </p>
      <template v-else-if="state.detail.value">
        <NucleoWorkOrderSummary :detail="state.detail.value" />
        <div class="grid gap-5 xl:grid-cols-[1.35fr_0.65fr]">
          <div class="space-y-5">
            <NucleoWorkOrderServices
              :services="state.detail.value.servicos"
              :selected-service-id="state.selectedServiceId.value"
              :progress="state.detail.value.progresso"
              :requested-at="state.detail.value.data_solicitacao"
              @select="state.selectedServiceId.value = $event"
              @work="openExecution"
            />
            <NucleoWorkOrderHistory :service="state.selectedService.value" />
          </div>
          <div class="space-y-5">
            <NucleoWorkOrderDocuments
              :documents="state.detail.value.documentos"
            />
            <NucleoWorkOrderPropertyDocuments
              :documents="state.detail.value.documentos"
              :transaction-id="
                state.detail.value.documentos.property_transaction_id
              "
              @view-property="viewProperty"
            />
          </div>
        </div>
      </template>
    </main>
    <NucleoWorkOrderServiceExecutionDialog
      :show="Boolean(executionService)"
      :service="executionService"
      :saving="state.saving.value"
      :transaction-id="state.detail.value?.documentos?.property_transaction_id"
      :parties="state.detail.value?.documentos?.parties"
      @close="closeExecution"
      @save="saveExecution"
    />
    <NucleoWorkOrderActionDialog
      :show="Boolean(dialog)"
      :action="dialog?.action"
      :service="dialog?.service"
      :saving="state.saving.value"
      @close="closeDialog"
      @save="saveAction"
    />
  </div>
</template>
